import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "hover:bg-primary/5 dark:hover:bg-primary-dark/5 transition-all duration-300 ease-out group" }
const _hoisted_2 = { class: "px-4 xl:px-6 py-4" }
const _hoisted_3 = { class: "flex items-center gap-3" }
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "min-w-0 flex-1" }
const _hoisted_6 = { class: "text-sm font-semibold text-slate-700 dark:text-slate-300 break-words" }
const _hoisted_7 = { class: "text-sm text-slate-700 dark:text-slate-300 break-all" }
const _hoisted_8 = { class: "hidden xl:table-cell px-4 xl:px-6 py-4 w-32" }
const _hoisted_9 = { class: "text-sm text-slate-700 dark:text-slate-300 truncate" }
const _hoisted_10 = { class: "px-4 xl:px-6 py-4 w-24" }
const _hoisted_11 = { class: "hidden xl:table-cell px-4 xl:px-6 py-4 w-36" }
const _hoisted_12 = { class: "text-sm text-slate-700 dark:text-slate-300" }
const _hoisted_13 = { class: "px-4 xl:px-6 py-4 text-right w-40" }
const _hoisted_14 = { class: "flex items-center justify-center gap-1 xl:gap-2" }

import { computed, ref, watch } from 'vue'
import { Eye, Lock, LockOpen, Trash, UserRoundCheck } from 'lucide-vue-next'
import api from '@/services/api'
import type { Employee } from '@/services/employeeService'
import StatusBadge from './StatusBadge.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeRow',
  props: {
    employee: {}
  },
  emits: ["view", "activate", "deactivate", "approve", "delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const handleAction = (action: 'view' | 'activate' | 'deactivate' | 'approve' | 'delete', id: number) => {

	switch (action) {
		case 'view':
			emit('view', id)
		break
		case 'activate':
			emit('activate', id)
		break
		case 'approve':
			emit('approve', id)
		break
		case 'deactivate':
			emit('deactivate', id)
		break
		case 'delete':
			emit('delete', id)
		break
	}
}

const displayName = computed(() => {
	if (props.employee.first_name && props.employee.last_name) {
		return `${props.employee.first_name} ${props.employee.last_name}`
	}
	return props.employee.name
})

const employeeInitials = computed(() => {
	if (props.employee.first_name && props.employee.last_name) {
		return (props.employee.first_name[0] + props.employee.last_name[0]).toUpperCase()
	}

	const name = String(props.employee.name || '').trim()
	if (!name) return 'EM'

	const names = name.split(/\s+/).filter(Boolean)
	if (names.length >= 2) {
		return (names[0][0] + names[names.length - 1][0]).toUpperCase()
	}
	return name.substring(0, 2).toUpperCase()
})

const avatarLoadFailed = ref(false)

const profileImageUrl = computed(() => {
	const raw = String(props.employee.profile_picture || '').trim()
	if (!raw || raw.toLowerCase() === 'null') return ''

	if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) {
		return raw
	}

	const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
	const baseNoApi = rawBase.replace(/\/api$/i, '')

	if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
	if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`

	return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
})

const shouldShowProfileImage = computed(() => Boolean(profileImageUrl.value) && !avatarLoadFailed.value)

watch(profileImageUrl, () => {
  	avatarLoadFailed.value = false
})

const onProfileImageError = () => {
  avatarLoadFailed.value = true
}

const avatarBgClass = computed(() => {
	const gradients = [
		'from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover',
		'from-success to-success-hover dark:from-success-dark dark:to-success-dark-hover',
		'from-info to-info-hover dark:from-info-dark dark:to-info-dark-hover',
		'from-warning to-warning-hover dark:from-warning-dark dark:to-warning-dark-hover',
		'from-danger to-danger-hover dark:from-danger-dark dark:to-danger-dark-hover'
	]
	const index = props.employee.id % gradients.length
	return gradients[index]
})

const formatDate = (dateString: string): string => {
	if (!dateString) return '—'
	const date = new Date(dateString)

	// Fallback if date is invalid
	if (isNaN(date.getTime())) return '—'

	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit'
	})
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("tr", _hoisted_1, [
    _createElementVNode("td", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (shouldShowProfileImage.value)
          ? (_openBlock(), _createElementBlock("img", {
              key: 0,
              src: profileImageUrl.value,
              alt: `${displayName.value} profile photo`,
              class: "h-10 w-10 xl:h-12 xl:w-12 rounded-full object-cover shadow-md flex-shrink-0 aspect-square",
              onError: onProfileImageError
            }, null, 40, _hoisted_4))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(["h-10 w-10 xl:h-12 xl:w-12 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0", avatarBgClass.value]),
              "aria-label": "Default profile avatar"
            }, _toDisplayString(employeeInitials.value), 3)),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, _toDisplayString(displayName.value), 1),
          _createElementVNode("div", _hoisted_7, _toDisplayString(__props.employee.email), 1)
        ])
      ])
    ]),
    _createElementVNode("td", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, _toDisplayString(__props.employee.phone || '—'), 1)
    ]),
    _createElementVNode("td", _hoisted_10, [
      _createVNode(StatusBadge, {
        status: __props.employee.status
      }, null, 8, ["status"])
    ]),
    _createElementVNode("td", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, _toDisplayString(formatDate(__props.employee.updated_at)), 1)
    ]),
    _createElementVNode("td", _hoisted_13, [
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (handleAction('view', __props.employee.id))),
          class: "p-1.5 xl:p-2 rounded-lg text-blue-600 dark:text-primary-dark/70 hover:text-primary dark:hover:text-primary-dark hover:bg-primary/10 dark:hover:bg-primary-dark/10 transition-all duration-200 hover:scale-110",
          title: "View"
        }, [
          _createVNode(_unref(Eye), { class: "h-4 w-4 xl:h-5 xl:w-5" })
        ]),
        (__props.employee.status === 'active')
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (handleAction('deactivate', __props.employee.id))),
              class: "p-1.5 xl:p-2 rounded-lg text-warning/70 dark:text-warning-dark/70 hover:text-warning dark:hover:text-warning-dark hover:bg-warning/10 dark:hover:bg-warning-dark/10 transition-all duration-200 hover:scale-110",
              title: "Deactivate"
            }, [
              _createVNode(_unref(Lock), { class: "h-4 w-4 xl:h-5 xl:w-5" })
            ]))
          : (__props.employee.status === 'inactive')
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                onClick: _cache[2] || (_cache[2] = ($event: any) => (handleAction('activate', __props.employee.id))),
                class: "p-1.5 xl:p-2 rounded-lg text-success/70 dark:text-success-dark/70 hover:text-success dark:hover:text-success-dark hover:bg-success/10 dark:hover:bg-success-dark/10 transition-all duration-200 hover:scale-110",
                title: "Activate"
              }, [
                _createVNode(_unref(LockOpen), { class: "h-4 w-4 xl:h-5 xl:w-5" })
              ]))
            : (__props.employee.status === 'pending')
              ? (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (handleAction('approve', __props.employee.id))),
                  class: "p-1.5 xl:p-2 rounded-lg text-primary/70 dark:text-success-dark/70 hover:text-success dark:hover:text-success-dark hover:bg-success/10 dark:hover:bg-success-dark/10 transition-all duration-200 hover:scale-110",
                  title: "Approve"
                }, [
                  _createVNode(_unref(UserRoundCheck), { class: "h-4 w-4 xl:h-5 xl:w-5" })
                ]))
              : _createCommentVNode("", true),
        (__props.employee.status === 'inactive' || __props.employee.status === 'pending')
          ? (_openBlock(), _createElementBlock("button", {
              key: 3,
              onClick: _cache[4] || (_cache[4] = ($event: any) => (handleAction('delete', __props.employee.id))),
              class: "p-1.5 xl:p-2 rounded-lg text-danger/70 dark:text-danger-dark/70 hover:text-danger dark:hover:text-danger-dark hover:bg-danger/10 dark:hover:bg-danger-dark/10 transition-all duration-200 hover:scale-110",
              title: "Delete"
            }, [
              _createVNode(_unref(Trash), { class: "h-4 w-4 xl:h-5 xl:w-5" })
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})